import { Component, ViewChild, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HelpListComponent } from '../../components/help-list/help-list.component';
import { HelpFormComponent } from '../../components/help-form/help-form.component';
import { HelpAttachmentsComponent } from '../../components/help-attachments/help-attachments.component';
import { HelpArticleService } from '../../services/help-article.service';
import { MenuService } from '../../../menus-management/services/menu.service';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { AppSettings } from '@services/settings/app-settings.service';
import { FormMode } from 'app/core/constants/form-mode';
import { PermissionService } from '@services/core/permission.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-help-management',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HelpListComponent,
    HelpFormComponent,
    HelpAttachmentsComponent,
    PageHeaderComponent,
    MobileFooterComponent
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './help-management.component.html',
  styleUrl: './help-management.component.scss'
})
export class HelpManagementComponent {
  @ViewChild(HelpListComponent) helpList!: HelpListComponent;

  selected: any = null;
  helpForm: FormGroup;
  mode = signal<FormMode>(FormMode.VIEW);
  formMode = FormMode;
  showDetail = false;

  public appSettings = inject(AppSettings);
  readonly perm = inject(PermissionService);
  private fb = inject(FormBuilder);
  private helpService = inject(HelpArticleService);
  private menuService = inject(MenuService);

  routeOptions: { label: string; value: string }[] = [{ label: '(inicio / general)', value: '' }];

  public Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true
  });

  constructor() {
    this.helpForm = this.fb.group({
      routePath: [''],
      title: ['', Validators.required],
      summary: [null],
      bodyMd: [''],
      audience: ['BOTH'],
      isActive: [true]
    });
    this.loadRouteOptions();
  }

  /** Rutas desde menús (fuente real de routePath). Incluye Bandeja de Entrada → solicitudes. */
  private loadRouteOptions(): void {
    this.menuService.get().subscribe({
      next: (menus) => {
        const list = Array.isArray(menus) ? menus : [];
        const byPath = new Map<string, string>();

        for (const m of list) {
          const value = HelpArticleService.normalizeRoutePath(m?.path ?? '');
          if (!value) continue;
          const name = String(m?.name || value).trim();
          if (!byPath.has(value)) {
            byPath.set(value, `${name} (${value})`);
          }
        }

        this.routeOptions = [
          { label: '(inicio / general)', value: '' },
          ...[...byPath.entries()]
            .map(([value, label]) => ({ label, value }))
            .sort((a, b) => a.label.localeCompare(b.label, 'es'))
        ];
      },
      error: () => {
        // Fallback: rutas Angular (path + matchers de prefijo)
        import('../../../../../app.routes').then(({ routes }) => {
          const opts: { label: string; value: string }[] = [];
          for (const r of routes) {
            if (r.path && r.path !== '**') {
              opts.push({
                label: String((r.data as any)?.['title'] || r.path),
                value: r.path
              });
              continue;
            }
            // matcher: prefixMatcher('solicitudes') → path "solicitudes"
            const matcherSrc = r.matcher?.toString?.() || '';
            const prefixes = [...matcherSrc.matchAll(/['"]([a-z0-9_-]+)['"]/gi)].map((m) => m[1]);
            if (prefixes.length) {
              const value = prefixes.join('/');
              opts.push({
                label: String((r.data as any)?.['title'] || value),
                value
              });
            }
          }
          this.routeOptions = [
            { label: '(inicio / general)', value: '' },
            ...opts.sort((a, b) => a.label.localeCompare(b.label, 'es'))
          ];
        });
      }
    });
  }

  onSelected(article: any): void {
    this.openEditor(article, FormMode.EDIT);
  }

  newArticle(): void {
    this.openEditor(null, FormMode.CREATE);
  }

  private openEditor(article: any | null, mode: FormMode): void {
    this.mode.set(mode);
    this.selected = article;
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();

    if (article) {
      this.helpService.getByUuid(article.uuid).subscribe({
        next: (full) => {
          this.selected = full;
          this.patchForm(full);
          this.showDetail = true;
        },
        error: () => {
          this.patchForm(article);
          this.showDetail = true;
        }
      });
    } else {
      this.helpForm.reset({
        routePath: '',
        title: '',
        summary: null,
        bodyMd: [
          '## Para qué sirve',
          '',
          '',
          '## Roles y acceso',
          '- **Rol A:** qué puede ver / hacer en esta pantalla.',
          '- **Rol B:** …',
          '',
          '## Cómo funciona',
          '',
          '',
          '## Paso a paso',
          '',
          '',
          '## Capturas',
          ''
        ].join('\n'),
        audience: 'BOTH',
        isActive: true
      });
      this.showDetail = true;
    }
  }

  private patchForm(article: any): void {
    this.helpForm.patchValue({
      routePath: article.routePath ?? '',
      title: article.title ?? '',
      summary: article.summary ?? null,
      bodyMd: article.bodyMd ?? '',
      audience: article.audience || 'BOTH',
      isActive: article.isActive !== false
    });
  }

  closeDetail(): void {
    this.showDetail = false;
    this.mode.set(FormMode.VIEW);
    this.selected = null;
  }

  private payload() {
    const raw = this.helpForm.getRawValue();
    return {
      routePath: HelpArticleService.normalizeRoutePath(raw.routePath ?? ''),
      title: String(raw.title || '').trim(),
      summary: raw.summary ? String(raw.summary).trim() : null,
      bodyMd: raw.bodyMd ?? '',
      audience: raw.audience || 'BOTH',
      isActive: !!raw.isActive
    };
  }

  save(): void {
    if (this.helpForm.invalid) {
      this.helpForm.markAllAsTouched();
      return;
    }
    const data = this.payload();
    if (this.mode() === FormMode.EDIT && this.selected?.uuid) {
      this.helpService.update(this.selected.uuid, data).subscribe({
        next: (updated) => {
          this.Toast.fire({ icon: 'success', title: 'Artículo actualizado' });
          this.selected = { ...this.selected, ...updated };
          this.helpList?.load();
          this.closeDetail();
        },
        error: (err) => {
          this.Toast.fire({
            icon: 'error',
            title: err?.error?.error || 'No se pudo actualizar'
          });
        }
      });
      return;
    }

    this.helpService.create(data).subscribe({
      next: (created) => {
        this.Toast.fire({ icon: 'success', title: 'Artículo creado' });
        this.selected = created;
        this.mode.set(FormMode.EDIT);
        this.helpList?.load();
        // Queda en edición para poder subir capturas tras crear
      },
      error: (err) => {
        this.Toast.fire({
          icon: 'error',
          title: err?.error?.error || 'No se pudo crear'
        });
      }
    });
  }
}
